<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useModuleStore } from '@/store/moduleStore'
import { useRouter } from 'vue-router'
import Card from 'primevue/card'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import ProgressSpinner from 'primevue/progressspinner'
import Dialog from 'primevue/dialog'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const toast = useToast()
const router = useRouter()
const moduleStore = useModuleStore()
const loading = ref(false)
const actionLoading = ref<string | null>(null)
const confirmDialog = ref(false)
const confirmAction = ref<{ type: 'deactivate' | 'golive'; moduleKey: string } | null>(null)

const modulesWithStatus = computed(() => moduleStore.modulesWithStatus)

const loadData = async () => {
  loading.value = true
  try {
    await moduleStore.initialize()
  } catch (error) {
    console.error('Error loading modules:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Modules could not be loaded',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

const getStatusSeverity = (status: string) => {
  switch (status) {
    case 'live':
      return 'success'
    case 'configuring':
      return 'warn'
    default:
      return 'secondary'
  }
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'live':
      return 'Live'
    case 'configuring':
      return 'Configuring'
    default:
      return 'Inactive'
  }
}

const activateModule = async (moduleKey: string) => {
  actionLoading.value = moduleKey
  try {
    await moduleStore.activateModule(moduleKey)
    toast.add({
      severity: 'success',
      summary: 'Activated',
      detail: 'Module has been activated. Please configure it now.',
      life: 3000
    })
  } catch (error: any) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.message,
      life: 5000
    })
  } finally {
    actionLoading.value = null
  }
}

const showDeactivateConfirm = (moduleKey: string) => {
  confirmAction.value = { type: 'deactivate', moduleKey }
  confirmDialog.value = true
}

const showGoLiveConfirm = (moduleKey: string) => {
  confirmAction.value = { type: 'golive', moduleKey }
  confirmDialog.value = true
}

const executeConfirmAction = async () => {
  if (!confirmAction.value) return

  const { type, moduleKey } = confirmAction.value
  confirmDialog.value = false
  actionLoading.value = moduleKey

  try {
    if (type === 'deactivate') {
      await moduleStore.deactivateModule(moduleKey)
      toast.add({
        severity: 'info',
        summary: 'Deactivated',
        detail: 'Module has been deactivated',
        life: 3000
      })
    } else if (type === 'golive') {
      await moduleStore.goLiveWithModule(moduleKey)
      toast.add({
        severity: 'success',
        summary: 'Live!',
        detail: 'Module is now live and visible to users',
        life: 3000
      })
    }
  } catch (error: any) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.message,
      life: 5000
    })
  } finally {
    actionLoading.value = null
    confirmAction.value = null
  }
}

const goToSettings = (moduleKey: string) => {
  const settingsRoutes: Record<string, string> = {
    leadgen: '/tenant/lead-generation/settings',
    faq: '/faqs_categories'
  }
  const route = settingsRoutes[moduleKey]
  if (route) {
    router.push(route)
  } else {
    toast.add({
      severity: 'warn',
      summary: 'Info',
      detail: 'Settings page for this module is not yet available',
      life: 3000
    })
  }
}

const getModuleIcon = (moduleKey: string): string => {
  const iconMap: Record<string, string> = {
    faq: 'pi pi-question-circle',
    leadgen: 'pi pi-envelope'
  }
  return iconMap[moduleKey] || 'pi pi-box'
}

const isModuleLocked = (moduleKey: string) => {
  return moduleStore.isModuleLockedByAdmin(moduleKey)
}

const getConfirmMessage = computed(() => {
  if (!confirmAction.value) return ''
  const module = modulesWithStatus.value.find((m) => m.key === confirmAction.value?.moduleKey)
  if (confirmAction.value.type === 'deactivate') {
    return `Are you sure you want to deactivate the module "${module?.name}"? All settings will be retained.`
  }
  return `Are you sure you want to make the module "${module?.name}" live? It will then be visible to all users.`
})

onMounted(() => {
  loadData()
})
</script>

<template>
  <div class="modules-settings-container w-full">
    <div v-if="loading" class="flex justify-content-center py-5">
      <ProgressSpinner />
    </div>

    <div v-else class="modules-grid">
      <Card
        v-for="module in modulesWithStatus"
        :key="module.key"
        :class="[
          'module-card',
          `module-${module.status}`,
          { 'module-locked': isModuleLocked(module.key) }
        ]"
      >
        <template #title>
          <div class="flex justify-content-between align-items-center">
            {{ module.name }}
            <Tag
              :value="getStatusLabel(module.status)"
              :severity="getStatusSeverity(module.status)"
            />
          </div>
        </template>
        <template #subtitle>
          <code class="module-key">{{ module.key }}</code>
        </template>
        <template #content>
          <!-- Status Stepper -->
          <div class="status-stepper mb-3">
            <div
              :class="[
                'step',
                {
                  active: module.status !== 'inactive',
                  completed: module.status === 'configuring' || module.status === 'live'
                }
              ]"
            >
              <div class="step-circle">
                <i v-if="module.status !== 'inactive'" class="pi pi-check"></i>
                <span v-else>1</span>
              </div>
              <span class="step-label">Activate</span>
            </div>
            <div
              class="step-line"
              :class="{ completed: module.status === 'configuring' || module.status === 'live' }"
            ></div>
            <div
              :class="[
                'step',
                { active: module.status === 'configuring', completed: module.status === 'live' }
              ]"
            >
              <div class="step-circle">
                <i v-if="module.status === 'live'" class="pi pi-check"></i>
                <span v-else>2</span>
              </div>
              <span class="step-label">Configure</span>
            </div>
            <div class="step-line" :class="{ completed: module.status === 'live' }"></div>
            <div
              :class="[
                'step',
                { active: module.status === 'live', completed: module.status === 'live' }
              ]"
            >
              <div class="step-circle">
                <i v-if="module.status === 'live'" class="pi pi-check"></i>
                <span v-else>3</span>
              </div>
              <span class="step-label">Live</span>
            </div>
          </div>

          <!-- Locked by admin banner -->
          <div v-if="isModuleLocked(module.key)" class="locked-banner">
            <i class="pi pi-lock"></i>
            <span>Locked by administrator</span>
          </div>

          <p class="module-description">{{ module.description }}</p>

          <!-- Validation Status -->
          <div v-if="module.status === 'configuring' && module.validationState" class="mt-3">
            <div v-if="module.validationState.isValid" class="validation-ok">
              <i class="pi pi-check-circle"></i>
              <span>Ready to go live</span>
            </div>
            <div v-else class="validation-pending">
              <i class="pi pi-exclamation-circle"></i>
              <span>Configuration incomplete</span>
              <ul v-if="module.validationState.missingFields.length > 0" class="missing-fields">
                <li v-for="field in module.validationState.missingFields" :key="field">
                  {{ field }}
                </li>
              </ul>
            </div>
          </div>
        </template>
        <template #footer>
          <div class="flex gap-2 justify-content-end flex-wrap">
            <!-- Inactive: Show Activate -->
            <template v-if="module.status === 'inactive'">
              <Button
                label="Activate"
                severity="success"
                @click="activateModule(module.key)"
                :loading="actionLoading === module.key"
                :disabled="isModuleLocked(module.key)"
              />
            </template>

            <!-- Configuring: Show Settings, Go Live, Deactivate -->
            <template v-else-if="module.status === 'configuring'">
              <Button
                label="Configure"
                severity="info"
                @click="goToSettings(module.key)"
                :disabled="isModuleLocked(module.key)"
              />
              <Button
                label="Go Live"
                severity="success"
                @click="showGoLiveConfirm(module.key)"
                :disabled="!module.validationState?.isValid || isModuleLocked(module.key)"
                :loading="actionLoading === module.key"
                v-tooltip.top="!module.validationState?.isValid ? 'Please configure first' : ''"
              />
              <Button
                severity="danger"
                @click="showDeactivateConfirm(module.key)"
                v-tooltip.top="'Deactivate Module'"
                :disabled="isModuleLocked(module.key)"
              >
                <template #icon>
                  <FontAwesomeIcon icon="fa-solid fa-trash-can" />
                </template>
              </Button>
            </template>

            <!-- Live: Show Settings, Deactivate -->
            <template v-else-if="module.status === 'live'">
              <Button
                label="Settings"
                severity="info"
                @click="goToSettings(module.key)"
                :disabled="isModuleLocked(module.key)"
              />
              <Button
                label="Deactivate"
                severity="danger"
                outlined
                @click="showDeactivateConfirm(module.key)"
                :loading="actionLoading === module.key"
                :disabled="isModuleLocked(module.key)"
              />
            </template>
          </div>
        </template>
      </Card>
    </div>

    <div v-if="!loading && modulesWithStatus.length === 0" class="text-center py-5">
      <i class="pi pi-box text-4xl text-gray-400 mb-3"></i>
      <p class="text-gray-500">No modules available</p>
    </div>

    <!-- Confirm Dialog -->
    <Dialog
      v-model:visible="confirmDialog"
      modal
      :header="confirmAction?.type === 'deactivate' ? 'Deactivate Module' : 'Go Live with Module'"
      :style="{ width: '400px' }"
    >
      <p>{{ getConfirmMessage }}</p>
      <template #footer>
        <Button label="Cancel" severity="secondary" @click="confirmDialog = false" />
        <Button
          :label="confirmAction?.type === 'deactivate' ? 'Deactivate' : 'Go Live'"
          :severity="confirmAction?.type === 'deactivate' ? 'danger' : 'success'"
          @click="executeConfirmAction"
        />
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
.modules-settings-container {
  padding: 1.5rem;
}

.modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}

.module-card {
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.module-inactive {
  border-color: #e0e0e0;
}

.module-locked {
  border-color: #dc3545 !important;
  opacity: 0.7;
}

.locked-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: #fee2e2;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 0.75rem;
}

.locked-banner i {
  font-size: 1rem;
}

.module-configuring {
  border-color: var(--tuev-light-blue);
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(245, 158, 11, 0.01) 100%);
}

.module-live {
  border-color: #10b981;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.01) 100%);
}

.module-header {
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px 6px 0 0;
}

.header-inactive {
  background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
}

.header-configuring {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
}

.header-live {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
}

.module-icon {
  font-size: 2.5rem;
  color: white;
}

.module-key {
  background-color: #f4f4f4;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.85rem;
  color: #666;
}

.module-description {
  min-height: 50px;
  color: #666;
  font-size: 0.9rem;
}

.validation-ok {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #10b981;
  font-size: 0.85rem;
}

.validation-pending {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  color: #f59e0b;
  font-size: 0.85rem;
}

.validation-pending i {
  margin-right: 0.25rem;
}

.missing-fields {
  margin: 0.5rem 0 0 1.25rem;
  padding: 0;
  font-size: 0.8rem;
  color: #666;
}

.missing-fields li {
  margin-bottom: 0.25rem;
}

/* Status Stepper Styles */
.status-stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 0;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  background: #e5e7eb;
  color: #9ca3af;
  transition: all 0.3s ease;
}

.step.active .step-circle,
.step.completed .step-circle {
  background: #10b981;
  color: white;
}

.step.active:not(.completed) .step-circle {
  background: #f59e0b;
  box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.2);
}

.step-label {
  font-size: 0.7rem;
  color: #9ca3af;
  text-align: center;
  white-space: nowrap;
}

.step.active .step-label,
.step.completed .step-label {
  color: #374151;
  font-weight: 500;
}

.step-line {
  flex: 1;
  height: 2px;
  background: #e5e7eb;
  margin: 0 0.5rem;
  margin-bottom: 1.25rem;
  min-width: 30px;
  transition: background 0.3s ease;
}

.step-line.completed {
  background: #10b981;
}
</style>
